import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import formatDate from "../utils/formatDate";

const getFirstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const toDateString = (dateStr) => {
  if (!dateStr) return undefined;
  return dateStr;
};

const CompanySchemes = () => {
  const navigate = useNavigate();

  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [designations, setDesignations] = useState([]);

  // ── Substate ──────────────────────────────────────────────────────────────
  const [allSubstates, setAllSubstates] = useState([]);
  const [substates, setSubstates] = useState([]);
  const [selectedSubstates, setSelectedSubstates] = useState([]);
  const [loadingSubstates, setLoadingSubstates] = useState(false);
  const [editSubstates, setEditSubstates] = useState([]);

  const [divisionCounts, setDivisionCounts] = useState({});
  const [stateCounts, setStateCounts] = useState({});
  const [editingDates, setEditingDates] = useState({});

  const [dealType, setDealType] = useState("deal-delight");
  const [schemeDraft, setSchemeDraft] = useState({
    name: "",
    start_date: getFirstOfMonth(),
    end_date: "",
    deal_type: "deal-delight",
    scheme_type: "gift",
    gift_name: "",
    pob: "",
    gift: "",
    multiple_pob: "",
    multiple_gift: "",
    product_scope: "all",
    product_ids: [],
    is_monthly: false,
  });

  const [editingScheme, setEditingScheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingEditProducts, setLoadingEditProducts] = useState(false);

  const [editDraft, setEditDraft] = useState({
    designation_ids: [],
    state_ids: [],
    substate_ids: [],
    gift_name: "",
    pob: "",
    gift: "",
    multiple_pob: "",
    multiple_gift: "",
    product_ids: [],
    is_monthly: false,
  });

  // All products fetched for the edit scheme's division
  const [editAllProducts, setEditAllProducts] = useState([]);

  const [editFilteredProducts, setEditFilteredProducts] = useState([]);
  const [editDealType, setEditDealType] = useState("");

  const getDealType = (scheme) => {
    return Array.isArray(scheme.product_ids) && scheme.product_ids.length > 0
      ? "temptation"
      : "deal-delight";
  };

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/designations/`, {
          headers: getAuthHeaders(),
        });
        setDesignations(res.data || []);
      } catch (err) {
        console.error("Failed to fetch designations", err);
        setDesignations([]);
      }
    };
    fetchDesignations();
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const allStatesSelected =
    states.length > 0 && selectedStates.length === states.length;

  const handleSelectAllStates = () => {
    if (allStatesSelected) {
      setSelectedStates([]);
    } else {
      setSelectedStates(states.map((s) => String(s.id)));
    }
  };

  // ── Substate filter: all when nothing selected, filtered when state selected ─
  useEffect(() => {
    setSelectedSubstates([]);
    if (selectedStates.length === 0) {
      setSubstates(allSubstates);
    } else {
      const nums = selectedStates.map(Number);
      setSubstates(allSubstates.filter((ss) => nums.includes(Number(ss.state_id))));
    }
  }, [selectedStates, allSubstates]);

  // ── Edit form substate filter ──────────────────────────────────────────────
  useEffect(() => {
    if (!editingScheme) return;
    if (editDraft.state_ids.length === 0) {
      setEditSubstates(allSubstates);
    } else {
      const nums = editDraft.state_ids.map(Number);
      setEditSubstates(allSubstates.filter((ss) => nums.includes(Number(ss.state_id))));
    }
  }, [editDraft.state_ids, editingScheme, allSubstates]);

  const allSubstatesSelected =
    substates.length > 0 && selectedSubstates.length === substates.length;

  const handleSelectAllSubstates = () => {
    if (allSubstatesSelected) {
      setSelectedSubstates([]);
    } else {
      setSelectedSubstates(substates.map((ss) => String(ss.id)));
    }
  };

  // Fetch products based on selected divisions for Temptation scheme (add form)
  useEffect(() => {
    if (dealType === "temptation" && selectedDivisions.length > 0) {
      fetchProductsByDivisions();
    }
  }, [selectedDivisions, dealType]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [divRes, stateRes, empRes, schemesRes, subStateRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/employees`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/scheme`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/sub_states`, { headers: getAuthHeaders() }),
      ]);

      setDivisions(divRes.data || []);
      setStates(stateRes.data || []);
      setSchemes(schemesRes.data || []);
      setAllSubstates(subStateRes.data || []);
      if (divRes.data && divRes.data.length > 0) {
        setSelectedDivisions([divRes.data[0].name]);
      }

      const divCount = {};
      const stateCount = {};
      (empRes.data || []).forEach((emp) => {
        divCount[emp.division] = (divCount[emp.division] || 0) + 1;
        stateCount[emp.state_id] = (stateCount[emp.state_id] || 0) + 1;
      });
      setDivisionCounts(divCount);
      setStateCounts(stateCount);
      setEmployees(empRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsByDivisions = async () => {
    try {
      setLoadingProducts(true);
      const selectedDivisionIds = divisions
        .filter((d) => selectedDivisions.includes(d.name))
        .map((d) => d.id);

      if (selectedDivisionIds.length === 0) {
        setFilteredProducts([]);
        setSchemeDraft((prev) => ({ ...prev, product_ids: [] }));
        return;
      }

      const queryParams = selectedDivisionIds
        .map((id) => `division_id=${id}`)
        .join("&");

      const response = await axios.get(
        `${BASE_URL}/api/product/?${queryParams}`,
        { headers: getAuthHeaders() },
      );
      setFilteredProducts(response.data || []);
      setProducts(response.data || []);
      setSchemeDraft((prev) => ({ ...prev, product_ids: [] }));
    } catch (err) {
      toast.error("Failed to load products");
      setFilteredProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchSchemes = async () => {
    const res = await axios.get(`${BASE_URL}/api/scheme`, {
      headers: getAuthHeaders(),
    });
    setSchemes(res.data || []);
  };

  const fetchEditProductsForScheme = async (scheme) => {
    try {
      setLoadingEditProducts(true);
      const divisionId = scheme.division_id;
      if (!divisionId) {
        setEditAllProducts([]);
        return;
      }
      const response = await axios.get(
        `${BASE_URL}/api/product/?division_id=${divisionId}`,
        { headers: getAuthHeaders() },
      );
      const allProds = response.data || [];
      setEditAllProducts(allProds);
    } catch (err) {
      console.error("Error fetching edit products:", err);
      setEditAllProducts([]);
    } finally {
      setLoadingEditProducts(false);
    }
  };

  const handleSingleDivisionSelect = (divisionName) => {
    setSelectedDivisions([divisionName]);
  };

  const handleCheckboxChange = (value, selectedList, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value],
    );
  };

  const handleProductsSelectAll = () => {
    if ((schemeDraft.product_ids || []).length === filteredProducts.length) {
      setSchemeDraft((s) => ({ ...s, product_ids: [] }));
    } else {
      setSchemeDraft((s) => ({
        ...s,
        product_ids: filteredProducts.map((p) => p.id),
      }));
    }
  };

  const handleEditProductsSelectAll = () => {
    if ((editDraft.product_ids || []).length === editAllProducts.length) {
      setEditDraft((s) => ({ ...s, product_ids: [] }));
    } else {
      setEditDraft((s) => ({
        ...s,
        product_ids: editAllProducts.map((p) => p.id),
      }));
    }
  };

  const toggleProductInDraft = (pid) => {
    setSchemeDraft((s) => {
      const setHas = new Set(s.product_ids);
      if (setHas.has(pid)) setHas.delete(pid);
      else setHas.add(pid);
      return { ...s, product_ids: Array.from(setHas) };
    });
  };

  const toggleEditProductInDraft = (pid) => {
    setEditDraft((s) => {
      const setHas = new Set(s.product_ids);
      if (setHas.has(pid)) setHas.delete(pid);
      else setHas.add(pid);
      return { ...s, product_ids: Array.from(setHas) };
    });
  };

  const handleDealTypeChange = (type) => {
    setDealType(type);
    setSchemeDraft((prev) => ({
      ...prev,
      deal_type: type,
      product_scope: type === "temptation" ? "selected" : "all",
      product_ids: [],
    }));
    if (type === "deal-delight") {
      setFilteredProducts(products);
    }
  };

  const validProductIds = schemeDraft.product_ids.filter((pid) =>
    filteredProducts.some((p) => p.id === pid),
  );

  const saveScheme = async () => {
    if (selectedDivisions.length === 0 || selectedStates.length === 0) {
      toast.warning("Select at least one Division and one State");
      return;
    }

    if (dealType === "temptation" && schemeDraft.product_ids.length === 0) {
      toast.warning("Select at least one product for Temptation scheme");
      return;
    }

    try {
      setLoading(true);

      const divisionIds = divisions
        .filter((d) => selectedDivisions.includes(d.name))
        .map((d) => d.id);

      const isTemptation = dealType === "temptation";
      const schemeName =
        schemeDraft.name?.trim() ||
        (isTemptation ? "Temptation" : "Deal Delight");

      const payload = {
        division_id: Number(divisionIds[0]),
        state_ids: selectedStates.map(Number),
        substate_ids: selectedSubstates.map(Number),
        scheme_name: schemeName,
        deal_type: dealType,
        scheme_type: "gift",
        status: "active",
        gift_name: schemeDraft.gift_name || null,
        start_date: schemeDraft.start_date,
        end_date: schemeDraft.end_date || schemeDraft.start_date,
        product_scope: isTemptation ? "selected" : "all",
        product_ids: isTemptation ? validProductIds : [],
        is_monthly: schemeDraft.is_monthly,
      };

      if (!isTemptation) {
        payload.pob = Number(schemeDraft.pob);
        payload.gift = schemeDraft.gift;
        payload.multiple_pob = Number(schemeDraft.multiple_pob);
        payload.multiple_gift = schemeDraft.multiple_gift;
      }

      await axios.post(`${BASE_URL}/api/scheme`, payload, {
        headers: getAuthHeaders(),
      });

      toast.success("Scheme saved successfully!");
      fetchSchemes();

      // Reset form
      setSchemeDraft({
        name: "",
        start_date: getFirstOfMonth(),
        end_date: "",
        deal_type: dealType,
        scheme_type: "gift",
        gift_name: "",
        pob: "",
        gift: "",
        multiple_pob: "",
        multiple_gift: "",
        product_scope: dealType === "temptation" ? "selected" : "all",
        product_ids: [],
        is_monthly: false,
      });
      setSelectedDivisions([]);
      setSelectedStates([]);
      setSelectedSubstates([]);
    } catch (e) {
      console.error("Error saving scheme:", e);
      toast.error(e.response?.data?.message || "Failed to save scheme.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (schemeId, field, value) => {
    setEditingDates((prev) => ({
      ...prev,
      [schemeId]: {
        ...prev[schemeId],
        [field]: value,
      },
    }));
  };

  const isActiveScheme = (scheme) => {
    const today = new Date();
    return (
      new Date(scheme.start_date) <= today && new Date(scheme.end_date) >= today
    );
  };

  const updateScheme = async (schemeId) => {
    try {
      const scheme = schemes.find((s) => s.id === schemeId);
      const currentDealType = getDealType(scheme);
      const dates = editingDates[schemeId] || {};

      const payload = {
        state_ids: editDraft.state_ids.map(Number),
        substate_ids: editDraft.substate_ids.map(Number),
        gift_name: editDraft.gift_name || null,
        designation_ids: editDraft.designation_ids.map(Number),
        is_monthly: editDraft.is_monthly,
        start_date: dates.start_date || undefined,
        end_date: dates.end_date || undefined,
      };

      if (currentDealType === "deal-delight") {
        payload.pob = editDraft.pob ? Number(editDraft.pob) : null;
        payload.gift = editDraft.gift || null;
        payload.multiple_pob = editDraft.multiple_pob
          ? Number(editDraft.multiple_pob)
          : null;
        payload.multiple_gift = editDraft.multiple_gift || null;
      }

      if (currentDealType === "temptation") {
        payload.product_ids = editDraft.product_ids.map(Number);
      }

      await axios.put(`${BASE_URL}/api/scheme/${schemeId}`, payload, {
        headers: getAuthHeaders(),
      });

      toast.success("Scheme updated successfully");
      fetchSchemes();
      setEditingScheme(null);
      setEditAllProducts([]);
    } catch (error) {
      console.error("Error updating scheme:", error);
      toast.error(error.response?.data?.message || "Failed to update scheme");
    }
  };

  const sortedFilteredProducts = [...filteredProducts].sort((a, b) => {
    const nameA = (a.name || a.product_name || "").toLowerCase();
    const nameB = (b.name || b.product_name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const sortedEditAllProducts = [...editAllProducts].sort((a, b) => {
    const nameA = (a.name || a.product_name || "").toLowerCase();
    const nameB = (b.name || b.product_name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleDeleteScheme = async (id) => {
    if (!window.confirm("Delete this scheme?")) return;
    try {
      await axios.delete(`${BASE_URL}/api/scheme/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Scheme deleted");
      fetchSchemes();
    } catch (error) {
      console.error("Error deleting scheme:", error);
      toast.error(error.response?.data?.message || "Failed to delete scheme");
    }
  };

  const divisionNameById = (id) => {
    if (!id) return "-";
    const d = divisions.find((x) => String(x.id) === String(id));
    return d ? d.name : `#${id}`;
  };

  const stateNameById = (id) => {
    if (!id) return "-";
    const s = states.find((x) => String(x.id) === String(id));
    return s ? s.name : `#${id}`;
  };

  const getDivisionNamesForScheme = (scheme) => {
    if (scheme.division_names && Array.isArray(scheme.division_names))
      return scheme.division_names.join(", ");
    if (scheme.division_name) return scheme.division_name;
    if (scheme.division_id) return divisionNameById(scheme.division_id);
    return "-";
  };

  const sortedSchemes = [...schemes].sort((a, b) => {
    const dateA = new Date(a.start_date || a.created_at || 0);
    const dateB = new Date(b.start_date || b.created_at || 0);
    return dateB - dateA;
  });

  const handleEditClick = async (scheme) => {
    setEditingScheme(scheme.id);

    setEditDraft({
      state_ids: Array.isArray(scheme.state_ids)
        ? scheme.state_ids
        : scheme.state_id
          ? [scheme.state_id]
          : [],
      substate_ids: Array.isArray(scheme.substate_ids)
        ? scheme.substate_ids
        : scheme.substate_id
          ? [scheme.substate_id]
          : [],
      designation_ids: scheme.designation_ids || [],
      gift_name: scheme.gift_name || "",
      pob: scheme.pob || "",
      gift: scheme.gift || "",
      multiple_pob: scheme.multiple_pob || "",
      multiple_gift: scheme.multiple_gift || "",
      product_ids: Array.isArray(scheme.product_ids) ? scheme.product_ids : [],
      is_monthly: scheme.is_monthly || false,
    });

    setEditingDates({
      [scheme.id]: {
        start_date: scheme.start_date?.split("T")[0] || "",
        end_date: scheme.end_date?.split("T")[0] || "",
      },
    });

    if (getDealType(scheme) === "temptation") {
      await fetchEditProductsForScheme(scheme);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-2">
      <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800">
        <span className="text-blue-700">Gift </span>
        <span className="text-blue-500">Schemes</span>
      </h1>

      {/* Deal Type Selection */}
      <div className="flex items-center justify-center mb-6 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-sm">
        <div className="flex flex-wrap gap-4 justify-center">
          <label
            className={`flex items-center space-x-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${dealType === "deal-delight"
              ? "bg-white border-blue-500"
              : "bg-white border-gray-300 hover:border-gray-400"
              }`}
          >
            <input
              type="radio"
              name="deal-type"
              value="deal-delight"
              checked={dealType === "deal-delight"}
              onChange={(e) => handleDealTypeChange(e.target.value)}
              className="w-4 h-4 text-blue-600"
            />
            <span
              className={`font-medium ${dealType === "deal-delight" ? "text-blue-700" : "text-gray-700"
                }`}
            >
              Deal Delight
            </span>
          </label>
          <label
            className={`flex items-center space-x-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${dealType === "temptation"
              ? "bg-white border-purple-500"
              : "bg-white border-gray-300 hover:border-gray-400"
              }`}
          >
            <input
              type="radio"
              name="deal-type"
              value="temptation"
              checked={dealType === "temptation"}
              onChange={(e) => handleDealTypeChange(e.target.value)}
              className="w-4 h-4 text-purple-600"
            />
            <span
              className={`font-medium ${dealType === "temptation" ? "text-purple-700" : "text-gray-700"
                }`}
            >
              Temptation
            </span>
          </label>
        </div>
      </div>

      {/* Division and State Selection */}
      <div className="dcr-leave-type-filter-container mb-6">
        <div className="dcr-leave-type-division-section bg-white p-2 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <label className="font-semibold text-gray-700 text-sm">
              Division
            </label>
          </div>
          <div className="dcr-leave-type-division-list grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {divisions.map((d) => {
              const isSelected = selectedDivisions[0] === d.name;
              return (
                <div
                  key={d.id}
                  className={`flex items-center p-2 rounded cursor-pointer ${isSelected}`}
                  onClick={() => handleSingleDivisionSelect(d.name)}
                >
                  <input
                    type="checkbox"
                    name="division"
                    checked={isSelected}
                    readOnly
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="ml-2 flex-1 text-sm text-gray-700">
                    {d.name}
                    <span className="text-gray-500 ml-1">
                      ({divisionCounts[d.name] || 0})
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dcr-leave-type-state-section bg-white p-2 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <label className="flex items-center gap-2 font-semibold text-gray-700 text-sm">
              <input
                type="checkbox"
                checked={allStatesSelected}
                onChange={handleSelectAllStates}
                className="w-4 h-4 text-blue-600"
              />
              State ({selectedStates.length} selected)
            </label>
          </div>
          <div className="dcr-leave-type-state-list grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {states.map((s) => (
              <div
                key={s.id}
                className="flex items-center p-1.5 hover:bg-gray-50 rounded"
              >
                <input
                  type="checkbox"
                  id={`state-${s.id}`}
                  checked={selectedStates.includes(String(s.id))}
                  onChange={() =>
                    handleCheckboxChange(
                      String(s.id),
                      selectedStates,
                      setSelectedStates,
                    )
                  }
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label
                  htmlFor={`state-${s.id}`}
                  className="ml-2 text-sm text-gray-700 cursor-pointer flex-1"
                >
                  {s.name}
                  <span className="ml-1 text-xs text-gray-500">
                    ({stateCounts[s.id] || 0})
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* SUB-STATE BOX — NEW */}
        <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-200 max-w-[320px]">
          <div className="flex items-center gap-2 mb-2">
            <label className="flex items-center gap-2 font-semibold text-gray-700 text-sm">
              {substates.length > 0 && (
                <input
                  type="checkbox"
                  checked={allSubstatesSelected}
                  onChange={handleSelectAllSubstates}
                  className="w-4 h-4 text-blue-600"
                />
              )}
              Sub-State ({selectedSubstates.length} selected)
            </label>
          </div>
          {loadingSubstates ? (
            <div className="flex items-center gap-2 py-3 px-1">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="text-xs text-gray-500">Loading sub-states…</span>
            </div>
          ) : substates.length === 0 ? (
            <p className="text-xs text-gray-400 italic px-1">No sub-states available</p>
          ) : (
            <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto">
              {substates.map((ss) => (
                <div key={ss.id} className="flex items-center p-1.5 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    id={`substate-${ss.id}`}
                    checked={selectedSubstates.includes(String(ss.id))}
                    onChange={() =>
                      handleCheckboxChange(String(ss.id), selectedSubstates, setSelectedSubstates)
                    }
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label
                    htmlFor={`substate-${ss.id}`}
                    className="ml-2 text-sm text-gray-700 cursor-pointer flex-1"
                  >
                    {ss.name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* END SUB-STATE BOX */}

      </div>

      <div className="flex items-center gap-6 mb-4">
        {/* Gift Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Gift Name
          </label>
          <input
            type="text"
            value={schemeDraft.gift_name}
            onChange={(e) =>
              setSchemeDraft((s) => ({ ...s, gift_name: e.target.value }))
            }
            placeholder="Gift name"
            className="h-9 w-48 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
        </div>

        {/* Monthly Checkbox */}
        <div className="flex items-center gap-2 mt-6">
          <input
            type="checkbox"
            checked={schemeDraft.is_monthly}
            onChange={(e) =>
              setSchemeDraft((s) => ({ ...s, is_monthly: e.target.checked }))
            }
            className="w-4 h-4 text-blue-600"
          />
          <label className="text-sm font-semibold text-gray-700">
            Monthly Scheme
          </label>
        </div>
      </div>

      {/* Deal Delight Section */}
      {dealType === "deal-delight" && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center justify-center">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            Deal Delight Configuration
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  POB Quantity
                </label>
                <input
                  type="number"
                  min="0"
                  value={schemeDraft.pob}
                  onChange={(e) =>
                    setSchemeDraft((s) => ({ ...s, pob: e.target.value }))
                  }
                  placeholder="Enter POB quantity"
                  className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Gift Quantity
                </label>
                <input
                  type="text"
                  value={schemeDraft.gift}
                  onChange={(e) =>
                    setSchemeDraft((s) => ({ ...s, gift: e.target.value }))
                  }
                  placeholder="Enter gift quantity"
                  className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Multiple POB
                </label>
                <input
                  type="number"
                  min="0"
                  value={schemeDraft.multiple_pob}
                  onChange={(e) =>
                    setSchemeDraft((s) => ({
                      ...s,
                      multiple_pob: e.target.value,
                    }))
                  }
                  placeholder="Enter multiple POB"
                  className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Multiple Gift
                </label>
                <input
                  type="text"
                  value={schemeDraft.multiple_gift}
                  onChange={(e) =>
                    setSchemeDraft((s) => ({
                      ...s,
                      multiple_gift: e.target.value,
                    }))
                  }
                  placeholder="Enter multiple gift"
                  className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Temptation Section - Products Selection (Add Form) */}
      {dealType === "temptation" && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center justify-center">
            <span className="inline-block w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
            Temptation - Product Selection *
          </h2>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200 shadow-sm">
            {selectedDivisions.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500 border border-gray-200 rounded-lg bg-white">
                Please select at least one division to view products
              </div>
            ) : loadingProducts ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                <p className="mt-2 text-sm text-gray-500">
                  Loading products...
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3 text-xs">
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-semibold">Products:</span>
                    <span>{filteredProducts.length} available</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-semibold">Selected:</span>
                    <span>{schemeDraft.product_ids.length}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={handleProductsSelectAll}
                    className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded"
                  >
                    {schemeDraft.product_ids.length ===
                      filteredProducts.length && filteredProducts.length > 0
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                  <span className="text-[11px] text-gray-500">
                    Products filtered by selected divisions
                  </span>
                </div>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-6 text-sm text-gray-500 border border-gray-200 rounded-lg bg-white">
                    No products found
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg bg-white p-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {sortedFilteredProducts.map((p) => {
                        const selected = schemeDraft.product_ids.includes(p.id);
                        return (
                          <div
                            key={p.id}
                            onClick={() => toggleProductInDraft(p.id)}
                            className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer transition ${selected
                              ? "bg-purple-50 border-purple-400"
                              : "hover:bg-gray-50 border-gray-200"
                              }`}
                          >
                            <div
                              className={`w-4 h-4 flex items-center justify-center rounded border-2 ${selected
                                ? "bg-blue-600 border-blue-600"
                                : "border-blue-600"
                                }`}
                            >
                              {selected && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-gray-700 truncate">
                                {p.name || p.product_name || `Product #${p.id}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-6 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 whitespace-nowrap">
            Start Date
          </label>
          <input
            type="date"
            value={schemeDraft.start_date}
            onChange={(e) =>
              setSchemeDraft((s) => ({ ...s, start_date: e.target.value }))
            }
            className="h-9 w-40 px-2 text-sm border border-gray-300 rounded-lg"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 whitespace-nowrap">
            End Date
          </label>
          <input
            type="date"
            value={schemeDraft.end_date}
            min={schemeDraft.start_date}
            onChange={(e) =>
              setSchemeDraft((s) => ({ ...s, end_date: e.target.value }))
            }
            className="h-9 w-40 px-2 text-sm border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <CustomButton
          onClick={saveScheme}
          variant="save"
          className="flex items-center gap-2 px-6 py-2.5"
          disabled={loading}
        >
          {loading ? <>Saving...</> : "Submit"}
        </CustomButton>
        <CustomButton
          onClick={() => navigate(`/dashboard`)}
          variant="cancel"
          className="px-6 py-2.5"
        >
          Cancel
        </CustomButton>
      </div>

      {/* ================= SCHEMES HISTORY ================= */}
      {schemes.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-bold text-center mb-4">
            <span className="text-blue-700">Schemes </span>
            <span className="text-blue-500">History</span>
          </h2>

          <div className="bg-white border shadow-sm overflow-x-auto">
            <table className="w-full table-auto border-collapse text-[11px]">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-2 py-2 text-center">S.No</th>
                  <th className="px-2 py-2 text-left">Scheme</th>
                  <th className="px-2 py-2 text-center">Monthly</th>
                  <th className="px-2 py-2 text-left">Gift Name</th>
                  <th className="px-2 py-2 text-left">Division</th>
                  <th className="px-2 py-2 text-left">States</th>
                  <th className="px-2 py-2 text-left">Sub-States</th>
                  <th className="px-2 py-2 text-center">POB</th>
                  <th className="px-2 py-2 text-center">Gift</th>
                  <th className="px-2 py-2 text-center">M-POB</th>
                  <th className="px-2 py-2 text-center">M-Gift</th>
                  <th className="px-2 py-2 text-left">Products</th>
                  <th className="px-2 py-2 text-left">Start Date</th>
                  <th className="px-2 py-2 text-left">End Date</th>
                  <th className="px-2 py-2 text-center">Action</th>
                  <th className="px-2 py-2 text-center">Remove</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {sortedSchemes.map((scheme, index) => {
                  const isEdit = editingScheme === scheme.id;
                  const active = isActiveScheme(scheme);
                  const schemeDealType = getDealType(scheme);

                  return (
                    <tr
                      key={scheme.id}
                      className={`align-top ${isEdit
                        ? "bg-white"
                        : active
                          ? "bg-green-200"
                          : "bg-red-200"
                        }`}
                    >
                      <td className="px-2 py-2 text-center font-semibold">
                        {index + 1}
                      </td>

                      {/* SCHEME NAME */}
                      <td className="px-2 py-2 font-medium">
                        {scheme.scheme_name}
                      </td>

                      {/* MONTHLY */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <input
                            type="checkbox"
                            checked={editDraft.is_monthly}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                is_monthly: e.target.checked,
                              }))
                            }
                            className="w-4 h-4 text-blue-600 cursor-pointer"
                            title={editDraft.is_monthly ? "Yes" : "No"}
                          />
                        ) : scheme.is_monthly ? (
                          <span className="text-green-700 font-semibold">
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </td>

                      {/* GIFT NAME */}
                      <td className="px-2 py-2">
                        {isEdit ? (
                          <input
                            value={editDraft.gift_name}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                gift_name: e.target.value,
                              }))
                            }
                            className="w-full border rounded text-xs px-1"
                          />
                        ) : (
                          scheme.gift_name || "-"
                        )}
                      </td>

                      {/* DIVISION */}
                      <td className="px-2 py-2">
                        {getDivisionNamesForScheme(scheme)}
                      </td>

                      {/* STATES */}
                      <td className="px-2 py-2">
                        {isEdit ? (
                          <div className="max-h-40 overflow-y-auto border rounded p-1">
                            <label className="flex gap-1 items-center font-semibold text-blue-700 border-b pb-1 mb-1">
                              <input
                                type="checkbox"
                                checked={
                                  states.length > 0 &&
                                  editDraft.state_ids.length === states.length
                                }
                                onChange={() =>
                                  setEditDraft((p) => ({
                                    ...p,
                                    state_ids:
                                      p.state_ids.length === states.length
                                        ? []
                                        : states.map((s) => s.id),
                                  }))
                                }
                              />
                              Select All ({states.length})
                            </label>
                            {states.map((s) => (
                              <label
                                key={s.id}
                                className="flex gap-1 items-center"
                              >
                                <input
                                  type="checkbox"
                                  checked={editDraft.state_ids.includes(s.id)}
                                  onChange={() =>
                                    setEditDraft((p) => ({
                                      ...p,
                                      state_ids: p.state_ids.includes(s.id)
                                        ? p.state_ids.filter(
                                          (id) => id !== s.id,
                                        )
                                        : [...p.state_ids, s.id],
                                    }))
                                  }
                                />
                                {s.name}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <ol className="list-decimal pl-4">
                            {scheme.state_name || "-"}
                          </ol>
                        )}
                      </td>

                      {/* SUB-STATES — NEW COLUMN */}
                      <td className="px-2 py-2">
                        {isEdit ? (
                          <div className="max-h-40 overflow-y-auto border rounded p-1 min-w-[130px]">
                            <label className="flex gap-1 items-center font-semibold text-blue-700 border-b pb-1 mb-1">
                              <input
                                type="checkbox"
                                checked={
                                  editSubstates.length > 0 &&
                                  editDraft.substate_ids.length === editSubstates.length
                                }
                                onChange={() =>
                                  setEditDraft((p) => ({
                                    ...p,
                                    substate_ids:
                                      p.substate_ids.length === editSubstates.length
                                        ? []
                                        : editSubstates.map((ss) => ss.id),
                                  }))
                                }
                              />
                              Select All ({editSubstates.length})
                            </label>
                            {editSubstates.map((ss) => (
                              <label key={ss.id} className="flex gap-1 items-center hover:bg-gray-50 rounded px-1">
                                <input
                                  type="checkbox"
                                  checked={editDraft.substate_ids.includes(ss.id)}
                                  onChange={() =>
                                    setEditDraft((p) => ({
                                      ...p,
                                      substate_ids: p.substate_ids.includes(ss.id)
                                        ? p.substate_ids.filter((id) => id !== ss.id)
                                        : [...p.substate_ids, ss.id],
                                    }))
                                  }
                                />
                                <span className="text-xs">
                                  {ss.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (

                          <span className="text-xs">
                            {scheme.substate_name || "-"}
                          </span>

                        )}
                      </td>
                      {/* END SUB-STATES */}

                      {/* POB */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <input
                            type="number"
                            value={editDraft.pob}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                pob: e.target.value,
                              }))
                            }
                            className="w-full border rounded text-xs px-1"
                          />
                        ) : (
                          (scheme.pob ?? "-")
                        )}
                      </td>

                      {/* GIFT */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <input
                            type="text"
                            value={editDraft.gift}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                gift: e.target.value,
                              }))
                            }
                            className="w-full border rounded text-xs px-1"
                          />
                        ) : (
                          (scheme.gift ?? "-")
                        )}
                      </td>

                      {/* M-POB */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <input
                            type="number"
                            value={editDraft.multiple_pob}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                multiple_pob: e.target.value,
                              }))
                            }
                            className="w-full border rounded text-xs px-1"
                          />
                        ) : (
                          (scheme.multiple_pob ?? "-")
                        )}
                      </td>

                      {/* M-GIFT */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <input
                            type="text"
                            value={editDraft.multiple_gift}
                            onChange={(e) =>
                              setEditDraft((p) => ({
                                ...p,
                                multiple_gift: e.target.value,
                              }))
                            }
                            className="w-full border rounded text-xs px-1"
                          />
                        ) : (
                          (scheme.multiple_gift ?? "-")
                        )}
                      </td>

                      {/* PRODUCTS */}
                      <td className="px-2 py-2">
                        {schemeDealType === "temptation" ? (
                          isEdit ? (
                            loadingEditProducts ? (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
                                Loading...
                              </div>
                            ) : (
                              <div className="max-h-40 overflow-y-auto border rounded p-1 min-w-[140px]">
                                <label className="flex gap-1 items-center font-semibold text-blue-700 border-b pb-1 mb-1">
                                  <input
                                    type="checkbox"
                                    checked={
                                      editAllProducts.length > 0 &&
                                      editDraft.product_ids.length ===
                                      editAllProducts.length
                                    }
                                    onChange={handleEditProductsSelectAll}
                                  />
                                  Select All ({editAllProducts.length})
                                </label>
                                {sortedEditAllProducts.map((p) => (
                                  <label
                                    key={p.id}
                                    className="flex gap-1 items-center hover:bg-gray-50 rounded px-1"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={editDraft.product_ids.includes(
                                        p.id,
                                      )}
                                      onChange={() =>
                                        toggleEditProductInDraft(p.id)
                                      }
                                    />
                                    <span className="text-xs">
                                      {p.name || p.product_name || `#${p.id}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )
                          ) : (
                            <ol className="list-decimal pl-4">
                              {(scheme.products || []).map((p) => (
                                <li key={p.id}>{p.name}</li>
                              ))}
                            </ol>
                          )
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* START DATE */}
                      <td className="px-2 py-2">
                        {isEdit ? (
                          <input
                            type="date"
                            value={editingDates[scheme.id]?.start_date}
                            onChange={(e) =>
                              handleDateChange(
                                scheme.id,
                                "start_date",
                                e.target.value,
                              )
                            }
                            className="border rounded text-xs px-1"
                          />
                        ) : (
                          formatDate(scheme.start_date)
                        )}
                      </td>

                      {/* END DATE */}
                      <td className="px-2 py-2">
                        {isEdit ? (
                          <input
                            type="date"
                            value={editingDates[scheme.id]?.end_date}
                            onChange={(e) =>
                              handleDateChange(
                                scheme.id,
                                "end_date",
                                e.target.value,
                              )
                            }
                            className="border rounded text-xs px-1"
                          />
                        ) : (
                          formatDate(scheme.end_date)
                        )}
                      </td>

                      {/* ACTION */}
                      <td className="px-2 py-2 text-center">
                        {isEdit ? (
                          <>
                            <CustomButton
                              variant="save"
                              onClick={() => updateScheme(scheme.id)}
                              className="px-2 py-1 text-xs"
                            >
                              Update
                            </CustomButton>
                            <CustomButton
                              variant="cancel"
                              onClick={() => {
                                setEditingScheme(null);
                                setEditAllProducts([]);
                              }}
                              className="px-2 py-1 text-xs mt-1"
                            >
                              Cancel
                            </CustomButton>
                          </>
                        ) : (
                          <CustomButton
                            variant="edit"
                            onClick={() => handleEditClick(scheme)}
                            className="px-2 py-1 text-xs"
                          >
                            Edit
                          </CustomButton>
                        )}
                      </td>

                      {/* DELETE */}
                      <td className="px-2 py-2 text-center">
                        <CustomButton
                          variant="delete"
                          onClick={() => handleDeleteScheme(scheme.id)}
                          className="px-2 py-1 text-xs"
                        >
                          Delete
                        </CustomButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySchemes;